// 在庫調整（破損・ロス・理由コード付き）— Stocky parity v2 Step 1。
// 調整は必ず Shopify の在庫（available）へ即時反映される＝「ローカルだけ調整」
// というモードは無い（ローカル DB は Shopify 同期の鏡なので、書かない調整は
// 次回同期で消えるため）。全プランで利用可・主拠点のみ（v1）。
import { apiFetch } from "./client";

export type PublicBigIntIdInput = string | number;

const MAX_SIGNED_BIGINT_ID = 9_223_372_036_854_775_807n;

/**
 * Normalize a public DB/Shopify identifier without ever rounding through
 * Number(). Safe numeric values are accepted only for an SPA-first rollout
 * against the previous backend.
 */
export function canonicalPublicBigIntId(
  value: unknown,
  label = "identifier",
): string {
  if (typeof value === "number" && Number.isSafeInteger(value) && value > 0) {
    return String(value);
  }
  if (
    typeof value === "string" &&
    /^[1-9][0-9]*$/.test(value) &&
    value.length <= 19 &&
    BigInt(value) <= MAX_SIGNED_BIGINT_ID
  ) {
    return value;
  }
  throw new TypeError(`${label} must be a canonical positive BIGINT`);
}

export interface AdjustableVariant {
  shopify_variant_id: string;
  sku: string | null;
  title: string | null;
  product_title: string | null;
  available: number | null;
}

export interface AdjustmentItem {
  shopify_variant_id: string;
  sku: string | null;
  title: string | null;
  current_product_title?: string | null;
  current_variant_title?: string | null;
  delta: number;
  before_qty: number | null;
  after_qty: number | null;
}

export interface InventoryAdjustment {
  id: PublicBigIntIdInput;
  location_id: string | null;
  location_name: string | null;
  reason: string;
  note: string | null;
  applied_at: string;
  items: AdjustmentItem[];
}

export interface CreateAdjustmentInput {
  location_id?: PublicBigIntIdInput | null;
  reason: string;
  note?: string;
  items: { shopify_variant_id: PublicBigIntIdInput; delta: number }[];
}

function normalizeAdjustableVariant(value: unknown): AdjustableVariant {
  if (value === null || typeof value !== "object") {
    throw new TypeError("adjustable variant response is invalid");
  }
  const row = value as Omit<AdjustableVariant, "shopify_variant_id"> & {
    shopify_variant_id: unknown;
  };
  return {
    ...row,
    shopify_variant_id: canonicalPublicBigIntId(
      row.shopify_variant_id,
      "shopify_variant_id",
    ),
  };
}

function normalizeInventoryAdjustment(value: unknown): InventoryAdjustment {
  if (value === null || typeof value !== "object") {
    throw new TypeError("inventory adjustment response is invalid");
  }
  const adjustment = value as Omit<
    InventoryAdjustment,
    "id" | "location_id" | "items"
  > & {
    id: unknown;
    location_id: unknown;
    items: Array<
      Omit<AdjustmentItem, "shopify_variant_id"> & {
        shopify_variant_id: unknown;
      }
    >;
  };
  if (!Array.isArray(adjustment.items)) {
    throw new TypeError("inventory adjustment items are invalid");
  }
  return {
    ...adjustment,
    id: canonicalPublicBigIntId(adjustment.id, "adjustment_id"),
    location_id:
      adjustment.location_id === null
        ? null
        : canonicalPublicBigIntId(adjustment.location_id, "location_id"),
    items: adjustment.items.map((item) => ({
      ...item,
      shopify_variant_id: canonicalPublicBigIntId(
        item.shopify_variant_id,
        "shopify_variant_id",
      ),
    })),
  };
}

/** 理由コード → 表示ラベル。値は backend ADJUSTMENT_REASONS（=Shopify が受理する値）と一致させる。 */
export const ADJUSTMENT_REASON_LABELS: Record<string, string> = {
  damaged: "破損",
  shrinkage: "ロス（紛失・盗難）",
  correction: "記録の訂正",
  quality_control: "検品・品質保留",
  other: "その他",
};

export async function listAdjustableVariants(
  query?: string,
  limit = 500,
): Promise<AdjustableVariant[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (query?.trim()) params.set("q", query.trim());
  const data = await apiFetch<{ variants: unknown[] }>(
    `/api/inventory-adjustments/variants?${params.toString()}`,
  );
  return data.variants.map(normalizeAdjustableVariant);
}

/** URLなどから渡された Shopify variant ID を、自店の現存商品として完全一致確認する。 */
export async function getAdjustableVariantByShopifyId(
  shopifyVariantId: PublicBigIntIdInput,
): Promise<AdjustableVariant | null> {
  const canonicalId = canonicalPublicBigIntId(
    shopifyVariantId,
    "shopify_variant_id",
  );
  const params = new URLSearchParams({
    shopify_variant_id: canonicalId,
    limit: "1",
  });
  const data = await apiFetch<{ variants: unknown[] }>(
    `/api/inventory-adjustments/variants?${params.toString()}`,
  );
  return (
    data.variants
      .map(normalizeAdjustableVariant)
      .find((variant) => variant.shopify_variant_id === canonicalId) ?? null
  );
}

export async function listInventoryAdjustments(): Promise<
  InventoryAdjustment[]
> {
  const data = await apiFetch<{ adjustments: unknown[] }>(
    "/api/inventory-adjustments",
  );
  return data.adjustments.map(normalizeInventoryAdjustment);
}

export async function createInventoryAdjustment(
  input: CreateAdjustmentInput,
  idempotencyKey: string,
): Promise<InventoryAdjustment> {
  const body: CreateAdjustmentInput = {
    ...input,
    ...(input.location_id === undefined
      ? {}
      : {
          location_id:
            input.location_id === null
              ? null
              : canonicalPublicBigIntId(input.location_id, "location_id"),
        }),
    items: input.items.map((item) => ({
      ...item,
      shopify_variant_id: canonicalPublicBigIntId(
        item.shopify_variant_id,
        "shopify_variant_id",
      ),
    })),
  };
  const result = await apiFetch<unknown>("/api/inventory-adjustments", {
    method: "POST",
    body,
    headers: { "Idempotency-Key": idempotencyKey },
  });
  return normalizeInventoryAdjustment(result);
}
